import { useEffect, useState, useCallback } from 'react'
import { clsx } from 'clsx'
import { Card, Badge, Button, Input, Select, RichSelect, ColorSwatch, Modal, SectionHeading, Divider, KVRow, ConfirmDialog, FormSection, Checkbox } from '../../../shared/components/UI'
import { cuttingApi, cuttingBatchApi, shadesApi, usersApi } from '../../../shared/lib/api'
import { getRows } from '../../../shared/lib/partyType'
import { Search, Filter, Eye, PencilLine, Trash2, Scissors, History, AlertCircle, PackagePlus, Check, Zap } from 'lucide-react'

const FALLBACK = '-'

export default function CuttingRecordsPage() {
  const [loading, setLoading] = useState(true)
  const [logs, setLogs]       = useState([])
  const [shades, setShades]   = useState([])
  const [masters, setMasters] = useState([])
  const [filters, setFilters] = useState({ rollCode: '', shadeId: '', masterId: '', page: 1 })
  const [total, setTotal]     = useState(0)
  const [pages, setPages]     = useState(1)
  
  const [selectedLog, setSelectedLog] = useState(null)
  const [editLog, setEditLog]         = useState(null)
  const [formData, setFormData]       = useState({ small_rolls_count: 0, weight_per_roll_kg: 0, action: 'return', wastage_reason: '' })
  const [saving, setSaving]           = useState(false)
  const [error, setError]             = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [isDeleting, setIsDeleting]       = useState(false)
  
  const [selectedIds, setSelectedIds]     = useState([])
  const [batching, setBatching]           = useState(false)
  const [showBatchConfirm, setShowBatchConfirm] = useState(false)
  const [showSplitModal, setShowSplitModal]     = useState(false)
  const [rollsPerBatch, setRollsPerBatch]     = useState('')

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [lRes, sRes, uRes] = await Promise.all([
        cuttingApi.listLogs(filters),
        shadesApi.list(),
        usersApi.list()
      ])
      
      const logData = lRes.data.data
      setLogs(logData.rows)
      setTotal(logData.total)
      setPages(logData.pages)
      
      setShades(getRows(sRes))
      setMasters(getRows(uRes).filter(u => u.role === 'metallic_master' || u.role === 'admin'))
    } catch (err) {
      console.error('Error loading cutting logs:', err)
      setError('Failed to load records.')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => { loadData() }, [loadData])

  const handleEdit = (log) => {
    setError('')
    setEditLog(log)
    setFormData({
      small_rolls_count: log.small_rolls_count,
      weight_per_roll_kg: parseFloat(log.weight_per_roll_kg),
      action: log.wastage_reason ? 'wastage' : 'return',
      wastage_reason: log.wastage_reason || ''
    })
  }

  const saveEdit = async () => {
    const newTotal = formData.small_rolls_count * formData.weight_per_roll_kg
    const maxPoss = parseFloat(editLog.roll.available_weight_kg) + parseFloat(editLog.total_cut_weight_kg)
    
    if (newTotal > maxPoss + 0.001) { // small buffer for float precision
      setError(`Cannot exceed available weight of ${maxPoss.toFixed(2)} KG`)
      return
    }

    setSaving(true)
    try {
      await cuttingApi.updateLog(editLog.id, formData)
      setEditLog(null)
      loadData()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update record.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = (id) => {
    setError('')
    setConfirmDelete(id)
  }

  const performDelete = async () => {
    if (!confirmDelete) return
    setIsDeleting(true)
    try {
      await cuttingApi.deleteLog(confirmDelete)
      setConfirmDelete(null)
      loadData()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete record.')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSelectLog = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      // Only select logs that are NOT already batched
      const unbatched = logs.filter(l => !l.batch_id).map(l => l.id)
      setSelectedIds(unbatched)
    } else {
      setSelectedIds([])
    }
  }

  const handleCreateBatch = async () => {
    setBatching(true)
    setError('')
    try {
      if (selectedIds.length === 1 && rollsPerBatch) {
        await cuttingBatchApi.create(selectedIds, parseInt(rollsPerBatch))
      } else {
        await cuttingBatchApi.create(selectedIds)
      }
      setSelectedIds([])
      setShowBatchConfirm(false)
      setShowSplitModal(false)
      setRollsPerBatch('')
      loadData()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create batch.')
    } finally {
      setBatching(false)
    }
  }

  const shadeOptions = [
    { label: 'All Shades', value: '' },
    ...shades
      .filter(s => (s.category === 'jari' || !s.category) && s.status === 'active')
      .map(s => ({ label: s.name, sub: s.code, color: s.color, value: s.id }))
  ]

  const masterOptions = [
    { label: 'All Masters', value: '' },
    ...masters.map(m => ({ label: m.name, value: m.id }))
  ]

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Cutting Records</h2>
          <p className="text-xs text-gray-400 mt-0.5">Track and manage all metallic roll cutting sessions</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="blue">{total} Total Records</Badge>
          <div className="w-px h-4 bg-gray-200 mx-1" />
          <Button variant="secondary" size="sm" onClick={loadData}>
            <History size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
          <AlertCircle size={18} />
          <p className="text-sm font-medium">{error}</p>
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {/* Filters */}
      <Card bodyClass="!p-6 !pb-2">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-6 lg:col-span-4">
            <FormSection label="Search Roll Code">
              <div className="relative group">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-500 transition-colors">
                  <Search size={14} />
                </span>
                <input 
                  className="form-input text-sm pl-9 bg-gray-50/50 border-gray-200 focus:bg-white transition-all" 
                  placeholder="Enter roll code (e.g. FY2526-0001)" 
                  value={filters.rollCode}
                  onChange={e => setFilters(p => ({ ...p, rollCode: e.target.value, page: 1 }))}
                />
              </div>
            </FormSection>
          </div>
          <div className="col-span-12 md:col-span-3 lg:col-span-3">
            <RichSelect 
              label="Shade / Color" 
              options={shadeOptions} 
              value={filters.shadeId}
              onChange={val => setFilters(p => ({ ...p, shadeId: val, page: 1 }))}
              placeholder="All Shades"
              renderItem={(opt) => (
                <div className="flex items-center gap-3">
                  {opt.value && <ColorSwatch color={opt.color} />}
                  <div className="flex flex-col gap-0">
                    <span className="text-sm font-semibold text-gray-700 leading-tight">{opt.label}</span>
                    {opt.sub && <span className="text-[10px] text-gray-400 font-mono tracking-tighter uppercase">{opt.sub}</span>}
                  </div>
                </div>
              )}
              className="bg-gray-50/50 border-gray-200 focus:bg-white !h-[42px] rounded-xl"
            />
          </div>
          <div className="col-span-12 md:col-span-3 lg:col-span-3">
            <RichSelect 
              label="Metallic Master" 
              options={masterOptions} 
              value={filters.masterId}
              onChange={val => setFilters(p => ({ ...p, masterId: val, page: 1 }))}
              placeholder="All Masters"
              className="bg-gray-50/50 border-gray-200 focus:bg-white !h-[42px] rounded-xl"
            />
          </div>
          <div className="col-span-12 lg:col-span-2 mb-4">
            <Button 
              variant="secondary" 
              className="w-full h-[42px] flex items-center justify-center gap-2 border-gray-100 hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700 transition-all"
              onClick={() => setFilters({ rollCode: '', shadeId: '', masterId: '', page: 1 })}
            >
              <Filter size={14} />
              Reset Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Main Table */}
      <Card bodyClass="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-10">
                  <Checkbox 
                    checked={selectedIds.length > 0 && selectedIds.length === logs.filter(l => !l.batch_id).length}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < logs.filter(l => !l.batch_id).length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Date</th>
                <th>Log ID</th>
                <th>Metallic Master</th>
                <th>Source Roll</th>
                <th>Shade</th>
                <th>Small Rolls</th>
                <th>Cut Wt.</th>
                <th>Merge Wt.</th>
                <th>Remainder</th>
                <th>Total Output</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="12" className="py-20 text-center text-gray-400">Loading records...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan="12" className="py-20 text-center text-gray-400">No cutting records found matching filters.</td></tr>
              ) : logs.map(log => (
                <tr key={log.id} className={clsx('group transition-colors', selectedIds.includes(log.id) ? 'bg-brand-50/30' : 'hover:bg-gray-50/50')}>
                  <td>
                    <Checkbox 
                      checked={selectedIds.includes(log.id)}
                      onChange={() => handleSelectLog(log.id)}
                      disabled={!!log.batch_id}
                    />
                  </td>
                  <td className="text-xs text-gray-500 font-medium">
                    <div className="flex flex-col">
                      <span>{new Date(log.created_at).toLocaleDateString()}</span>
                      <span className="text-[10px] text-gray-400 font-normal">{new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </td>
                  <td className="td-mono font-bold text-gray-400 text-[11px]">
                    {log.cutting_log_number || log.id.substring(0, 8).toUpperCase()}
                  </td>
                  <td className="font-semibold text-gray-700 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center text-[10px] font-bold">
                        {log.performer?.name?.slice(0, 2).toUpperCase()}
                      </div>
                      {log.performer?.name || FALLBACK}
                    </div>
                  </td>
                  <td className="td-mono font-bold text-brand-600">{log.roll?.roll_code}</td>
                  <td className="text-xs">
                    <Badge variant="blue">{log.roll?.shade?.name}</Badge>
                  </td>
                  <td className="text-sm font-semibold">{log.small_rolls_count}</td>
                  <td className="text-xs font-bold text-gray-600">{parseFloat(log.total_cut_weight_kg).toFixed(2)} KG</td>
                  <td className="text-xs font-bold text-brand-600">{parseFloat(log.merged_weight_kg || 0).toFixed(2)} KG</td>
                  <td>
                    {parseFloat(log.wastage_weight_kg) > 0 ? (
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-red-600">{parseFloat(log.wastage_weight_kg).toFixed(2)} KG</span>
                        <span className="text-[9px] text-red-400 uppercase font-bold leading-none">Wastage</span>
                      </div>
                    ) : parseFloat(log.returned_weight_kg) > 0 ? (
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-brand-600">{parseFloat(log.returned_weight_kg).toFixed(2)} KG</span>
                        <span className="text-[9px] text-brand-400 uppercase font-bold leading-none">Return</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300">-</span>
                    )}
                  </td>
                  <td className="text-sm font-bold text-gray-800">
                    {parseFloat(log.total_batch_weight || (parseFloat(log.total_cut_weight_kg) + parseFloat(log.merged_weight_kg || 0))).toFixed(2)} 
                    <span className="text-[10px] text-gray-400 font-normal"> KG</span>
                  </td>
                  <td>
                    {log.batch_id ? (
                      <Badge variant="gray">Batched</Badge>
                    ) : (parseFloat(log.remaining_weight_kg) > 0 && log.is_locked) ? (
                      <Badge variant="amber" className="!bg-amber-50 !text-amber-700 border-amber-100/50">
                        Batching — {parseFloat(log.remaining_weight_kg).toFixed(2)} KG Left
                      </Badge>
                    ) : log.is_locked ? (
                      <Badge variant="gray">Locked — Processed</Badge>
                    ) : (
                      <Badge variant="green">Open — Editable</Badge>
                    )}
                  </td>
                  <td>
                    <div className="flex justify-start gap-1 px-2">
                      <button 
                        onClick={() => setSelectedLog(log)}
                        className="p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-xl transition-all"
                        title="View Full Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        onClick={() => handleEdit(log)}
                        disabled={log.is_locked}
                        className={`p-2 rounded-xl transition-all ${log.is_locked ? 'text-gray-200 cursor-not-allowed' : 'text-gray-400 hover:text-amber-600 hover:bg-amber-50'}`}
                        title={log.is_locked ? "Processed in Bobbin Batch - Cannot Edit" : "Edit Record"}
                      >
                        <PencilLine size={16} />
                      </button>
                      <button 
                        onClick={() => handleDelete(log.id)}
                        disabled={log.is_locked}
                        className={`p-2 rounded-xl transition-all ${log.is_locked ? 'text-gray-200 cursor-not-allowed' : 'text-gray-400 hover:text-red-600 hover:bg-red-50'}`}
                        title={log.is_locked ? "Processed in Bobbin Batch - Cannot Delete" : "Delete Record"}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination placeholder */}
        {pages > 1 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/50">
            <p className="text-[11px] text-gray-400 font-medium tracking-wide">Showing page {filters.page} of {pages} ({total} total records)</p>
            <div className="flex gap-2">
              <Button 
                size="sm" variant="secondary" 
                disabled={filters.page === 1}
                onClick={() => setFilters(p => ({ ...p, page: p.page - 1 }))}
              >Previous</Button>
              <Button 
                size="sm" variant="secondary" 
                disabled={filters.page === pages}
                onClick={() => setFilters(p => ({ ...p, page: p.page + 1 }))}
              >Next</Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      {selectedLog && (
        <Modal 
          isOpen={!!selectedLog} 
          onClose={() => setSelectedLog(null)} 
          title="Cutting Record Details"
          size="md"
        >
          <div className="space-y-6">
            <div className="flex items-center justify-center p-6 bg-brand-50/50 rounded-2xl border border-brand-100 border-dashed">
              <div className="text-center">
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-brand-600 mb-2 mx-auto">
                  <Scissors size={24} />
                </div>
                <h3 className="text-lg font-bold text-gray-800">{selectedLog.small_rolls_count} Small Rolls</h3>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Total Output: {parseFloat(selectedLog.total_batch_weight || (parseFloat(selectedLog.total_cut_weight_kg) + parseFloat(selectedLog.merged_weight_kg || 0))).toFixed(2)} KG</p>
              </div>
            </div>

            <div className="p-5 bg-white border-2 border-brand-100/30 rounded-3xl shadow-sm relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-50 rounded-full -mr-16 -mt-16 opacity-50" />
              <p className="text-[10px] font-black text-brand-600 uppercase tracking-[0.2em] mb-4 pl-1 relative z-10">Batch Weight Breakdown</p>
              <div className="grid grid-cols-3 gap-3 relative z-10">
                <div className="bg-gray-50/80 p-4 rounded-2xl border border-gray-100 shadow-inner">
                  <div className="flex items-center gap-2 mb-1.5 opacity-60">
                    <Scissors size={12} className="text-gray-400" />
                    <p className="text-[9px] font-black text-gray-500 uppercase tracking-tighter">New Production</p>
                  </div>
                  <p className="text-lg font-black text-gray-800 leading-none">{parseFloat(selectedLog.total_cut_weight_kg).toFixed(2)} <span className="text-[10px] opacity-40">KG</span></p>
                </div>
                <div className="bg-brand-50/50 p-4 rounded-2xl border border-brand-100/50 shadow-inner">
                  <div className="flex items-center gap-2 mb-1.5 opacity-70">
                    <PackagePlus size={12} className="text-brand-400" />
                    <p className="text-[9px] font-black text-brand-500 uppercase tracking-tighter">Reused Merge</p>
                  </div>
                  <p className="text-lg font-black text-brand-600 leading-none">{parseFloat(selectedLog.merged_weight_kg || 0).toFixed(2)} <span className="text-[10px] opacity-40">KG</span></p>
                </div>
                <div className="bg-brand-600 p-4 rounded-2xl shadow-lg ring-4 ring-brand-100/50">
                  <div className="flex items-center gap-2 mb-1.5 opacity-80">
                    <Zap size={12} className="text-brand-100" />
                    <p className="text-[9px] font-black text-brand-100 uppercase tracking-tighter">Total Batch</p>
                  </div>
                  <p className="text-lg font-black text-white leading-none">{parseFloat(selectedLog.total_batch_weight || (parseFloat(selectedLog.total_cut_weight_kg) + parseFloat(selectedLog.merged_weight_kg || 0))).toFixed(2)} <span className="text-[10px] opacity-60">KG</span></p>
                </div>
              </div>
            </div>

            <SectionHeading>Process Context</SectionHeading>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Metallic Master</p>
                <p className="text-sm font-bold text-gray-800">{selectedLog.performer?.name}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Timestamp</p>
                <p className="text-sm font-bold text-gray-800">{new Date(selectedLog.created_at).toLocaleString()}</p>
              </div>
            </div>

            <Divider />
                 {/* Group A: New Production Identifiers */}
            <div className="space-y-3">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <Scissors size={14} className="text-gray-400" />
                  <SectionHeading className="!mb-0">New Production Identifiers</SectionHeading>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge variant="blue" size="xs" className="!bg-blue-50 !text-blue-700 font-black">{(selectedLog.children?.length || selectedLog.small_rolls_count)} New Rolls</Badge>
                  <Badge variant="blue" size="xs" className="!bg-blue-50 !text-blue-700 font-black">Total: {parseFloat(selectedLog.total_cut_weight_kg).toFixed(2)} KG</Badge>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 bg-gray-50/50 p-4 rounded-2xl border border-gray-100 max-h-[200px] overflow-y-auto custom-scrollbar">
                {(selectedLog.children?.length > 0 ? selectedLog.children : Array.from({ length: selectedLog.small_rolls_count })).map((roll, i) => {
                  const isReal = !!roll?.id
                  const rollCode = isReal ? roll.roll_code : `${selectedLog.roll?.roll_code}/${i + 1}`
                  const batch = isReal ? selectedLog.bobbinBatches?.find(b => b.roll_id === roll.id) : null
                  
                  return (
                    <div key={i} className={clsx(
                      "flex items-center justify-between px-3 py-2.5 bg-white border-2 rounded-xl shadow-sm group transition-all",
                      batch ? "border-green-100 bg-green-50/10" : "border-white hover:border-brand-200"
                    )}>
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] text-gray-400 font-black uppercase leading-none">New</span>
                          <span className={clsx("text-[12px] font-black font-mono tracking-tighter leading-none", batch ? "text-green-700" : "text-gray-800")}>{rollCode}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        {isReal ? (
                          <span className="text-[11px] text-brand-600 font-black">{parseFloat(roll.weight_pending_kg || roll.available_weight_kg).toFixed(2)} KG</span>
                        ) : (
                          <span className="text-[10px] text-gray-300 italic font-bold">{parseFloat(selectedLog.weight_per_roll_kg).toFixed(2)} KG</span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <Divider />
            
            <div className="space-y-1 pr-1">
              <KVRow label="Source Roll (Master)" value={selectedLog.roll?.roll_code} bold />
              <KVRow label="Shade / Color" value={selectedLog.roll?.shade?.name} />
              <KVRow label="Production Wt / Roll" value={`${parseFloat(selectedLog.weight_per_roll_kg).toFixed(2)} KG`} />
              <KVRow label="Master Start weight" value={`${parseFloat(selectedLog.roll_weight_before_kg).toFixed(2)} KG`} />
              <KVRow label="Master End weight" value={`${parseFloat(selectedLog.roll_weight_after_kg).toFixed(2)} KG`} />
              
              {selectedLog.merged_roll_id && (
                <KVRow label="Main Collector Code" value={selectedLog.mergedRoll?.roll_code} valueClass="text-brand-700 font-black" highlight />
              )}
              
              {/* Group B: Auxiliary Merged Rolls */}
              {selectedLog.merges?.length > 0 && (
                <div className="pt-4 space-y-3">
                  <div className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                      <PackagePlus size={14} className="text-brand-400" />
                      <SectionHeading className="!mb-0 text-brand-700">Auxiliary Merged Rolls (Reused)</SectionHeading>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="blue" size="xs" className="!bg-brand-50 !text-brand-700 font-black tracking-tight">{selectedLog.merges.length} Merged Roll</Badge>
                      <Badge variant="blue" size="xs" className="!bg-brand-50 !text-brand-700 font-black tracking-tight">Total: {parseFloat(selectedLog.merged_weight_kg || 0).toFixed(2)} KG</Badge>
                    </div>
                  </div>
                  
                  <div className="bg-brand-50/40 rounded-2xl p-4 border border-brand-100/50 space-y-2 max-h-[160px] overflow-y-auto custom-scrollbar">
                    {selectedLog.merges.map((m, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-white/80 p-2.5 rounded-xl border border-brand-100/30 shadow-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] font-black text-brand-400 uppercase tracking-tighter leading-none border border-brand-100 px-1 rounded bg-brand-50/50">Merged</span>
                          <span className="font-mono font-black text-xs text-brand-800 leading-none">{m.mergedRoll?.roll_code || m.sourceRoll?.roll_code}</span>
                        </div>
                        <span className="font-black text-brand-700 text-xs leading-none">
                          {((parseFloat(m.weight_contributed) > 0) 
                            ? parseFloat(m.weight_contributed) 
                            : (parseFloat(m.mergedRoll?.available_weight_kg) > 0)
                              ? parseFloat(m.mergedRoll?.available_weight_kg)
                              : (selectedLog.merges.length === 1 && parseFloat(selectedLog.merged_weight_kg) > 0)
                                ? parseFloat(selectedLog.merged_weight_kg)
                                : 0).toFixed(2)} KG
                        </span>
                      </div>
                    ))}
                    <p className="text-[10px] text-brand-400 italic mt-2 font-bold px-1 opacity-80">* Already cut in previous sessions; re-collected for this batch output.</p>
                  </div>
                </div>
              )}
          
              <Divider />
              
              {parseFloat(selectedLog.remaining_weight_kg) > 0 && selectedLog.is_locked && (
                <KVRow 
                  label="Remaining to Batch" 
                  value={`${parseFloat(selectedLog.remaining_weight_kg).toFixed(2)} KG`} 
                  valueClass="text-brand-600 font-black" 
                  highlight 
                />
              )}
              
              <KVRow 
                label="Action for Remainder (at Cutting)" 
                value={parseFloat(selectedLog.roll_weight_after_kg) === 0 && parseFloat(selectedLog.wastage_weight_kg || 0) <= 0 ? 'N/A - Fully Cut' : (selectedLog.merged_roll_id ? `Merged into ${selectedLog.mergedRoll?.roll_code}` : (selectedLog.returned_to_inventory && parseFloat(selectedLog.wastage_weight_kg || 0) > 0 ? 'Returned to Inventory + Wastage' : (selectedLog.returned_to_inventory ? 'Returned to Inventory' : 'Marked as Wastage')))} 
                valueClass={parseFloat(selectedLog.wastage_weight_kg || 0) > 0 ? 'text-red-600' : (parseFloat(selectedLog.roll_weight_after_kg) === 0 ? 'text-gray-400 font-medium' : (selectedLog.merged_roll_id ? 'text-brand-700' : (selectedLog.returned_to_inventory ? 'text-brand-600' : 'text-red-600')))}
              />
              {parseFloat(selectedLog.wastage_weight_kg || 0) > 0 && (
                <KVRow label="Wastage Weight" value={`${parseFloat(selectedLog.wastage_weight_kg || 0).toFixed(2)} KG`} valueClass="text-red-600 font-black" />
              )}
              {selectedLog.wastage_reason && (
                <KVRow label="Wastage Reason" value={selectedLog.wastage_reason} valueClass="text-red-500" />
              )}
            </div>

            {selectedLog.bobbinBatches?.length > 0 && (
              <div className="space-y-4 pt-2 animate-in fade-in slide-in-from-top-4 duration-500">
                <div className="flex items-center gap-3">
                  <Divider className="flex-1" />
                  <span className="text-[10px] font-black text-brand-600 uppercase tracking-[0.2em] whitespace-nowrap">Bobbin Processing Details</span>
                  <Divider className="flex-1" />
                </div>
                
                <div className="grid gap-2.5">
                  {selectedLog.bobbinBatches.map(batch => (
                    <div key={batch.id} className="p-3.5 bg-brand-50/40 border border-brand-100/50 rounded-2xl flex items-center justify-between group hover:border-brand-200 hover:bg-brand-50 transition-all duration-300 shadow-sm">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-brand-500 shadow-[0_0_8px_rgba(var(--brand-600),0.5)]" />
                          <span className="text-xs font-black text-gray-900 font-mono tracking-tight uppercase leading-none">{batch.batch_number}</span>
                        </div>
                        <span className="text-[10px] text-brand-600 font-bold uppercase tracking-widest pl-3.5">
                          Container: <span className="text-gray-900 font-black">{batch.container?.container_id || 'N/A'}</span>
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-black text-brand-700 leading-none">
                          {parseFloat(batch.total_weight_kg).toFixed(2)} 
                          <span className="text-[10px] font-medium opacity-50 ml-1">KG</span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-4">
              <Button onClick={() => setSelectedLog(null)} className="w-full">Close Details</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {editLog && (
        <Modal 
          isOpen={!!editLog} 
          onClose={() => setEditLog(null)} 
          title="Edit Cutting Record"
          size="sm"
          confirmClose={formData.small_rolls_count > 0}
        >
          <div className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-100 text-red-600 px-3 py-2 rounded-lg text-xs flex items-center gap-2">
                <AlertCircle size={14} />
                <span>{error}</span>
              </div>
            )}
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-100 rounded-lg text-amber-700">
              <AlertCircle size={18} />
              <p className="text-xs font-medium">Changes will automatically update the source roll's inventory balance.</p>
            </div>

            <Input 
              label="Small Rolls Count" 
              type="number" 
              value={formData.small_rolls_count}
              onChange={e => setFormData(p => ({ ...p, small_rolls_count: parseInt(e.target.value) }))}
            />
            <Input 
              label="Weight per Roll (KG)" 
              type="number" 
              value={formData.weight_per_roll_kg}
              onChange={e => setFormData(p => ({ ...p, weight_per_roll_kg: parseFloat(e.target.value) }))}
            />
            
            <Select 
              label="Remainder Action"
              options={[
                { label: 'Return to Inventory', value: 'return' },
                { label: 'Mark as Wastage', value: 'wastage' }
              ]}
              value={formData.action}
              onChange={e => setFormData(p => ({ ...p, action: e.target.value }))}
            />

            {formData.action === 'wastage' && (
              <Input 
                label="Wastage Reason" 
                placeholder="Why is it wasted?"
                value={formData.wastage_reason}
                onChange={e => setFormData(p => ({ ...p, wastage_reason: e.target.value }))}
              />
            )}

            {editLog && (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 space-y-2 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-medium">Original Roll Bag:</span>
                  <span className="text-gray-800 font-bold">{editLog.roll?.roll_code}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-medium">Max Possible for this cut:</span>
                  <span className="text-brand-600 font-bold">{(parseFloat(editLog.roll?.available_weight_kg) + parseFloat(editLog.total_cut_weight_kg)).toFixed(2)} KG</span>
                </div>
                <Divider className="!my-1.5" />
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600 font-semibold">New Total Cut:</span>
                  <span className={clsx(
                    'font-bold',
                    (formData.small_rolls_count * formData.weight_per_roll_kg) > (parseFloat(editLog.roll?.available_weight_kg) + parseFloat(editLog.total_cut_weight_kg)) 
                      ? 'text-red-600' 
                      : 'text-brand-700'
                  )}>
                    {(formData.small_rolls_count * formData.weight_per_roll_kg).toFixed(2)} KG
                  </span>
                </div>
              </div>
            )}

            <div className="pt-4 flex gap-3">
              <Button variant="secondary" onClick={() => setEditLog(null)} className="flex-1">Cancel</Button>
              <Button 
                onClick={saveEdit} 
                isLoading={saving} 
                className="flex-1"
                disabled={(formData.small_rolls_count * formData.weight_per_roll_kg) > (parseFloat(editLog.roll?.available_weight_kg) + parseFloat(editLog.total_cut_weight_kg)) + 0.001}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={performDelete}
        isLoading={isDeleting}
        title="Delete Record?"
        message="Are you sure you want to delete this cutting record? This will permanently revert the cut weight back to the source roll's inventory."
        confirmText="Yes, Delete"
        variant="danger"
        error={error}
      />

      {/* Create Batch Modal */}
      <ConfirmDialog
        isOpen={showBatchConfirm}
        onClose={() => setShowBatchConfirm(false)}
        onConfirm={handleCreateBatch}
        isLoading={batching}
        title="Create New Batch?"
        message={`This will group the ${selectedIds.length} selected cutting logs into a new uniquely numbered batch for further processing.`}
        confirmText="Confirm & Create Batch"
        icon={<PackagePlus className="text-brand-600" size={24} />}
      />

      {/* Floating Action Bar */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-4">
          <div className="bg-gray-900 border border-gray-800 text-white rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-6 min-w-[400px]">
            <div className="flex flex-col">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Selected Log(s)</span>
              <span className="text-xl font-black text-brand-400">{selectedIds.length}</span>
            </div>
            
            <div className="w-px h-10 bg-gray-700" />
            
            <div className="flex-1 flex items-center gap-3">
              <Button 
                variant="primary" 
                className="!bg-brand-600 hover:!bg-brand-500 shadow-lg shadow-brand-900/20 px-6"
                onClick={() => {
                  if (selectedIds.length === 1) {
                    setShowSplitModal(true)
                  } else {
                    setShowBatchConfirm(true)
                  }
                }}
              >
                <PackagePlus size={16} className="mr-2" />
                {selectedIds.length === 1 ? 'Batch / Split' : 'Create Batch'}
              </Button>
              <Button 
                variant="secondary" 
                className="!bg-transparent !border-gray-700 !text-gray-300 hover:!bg-gray-800"
                onClick={() => setSelectedIds([])}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Split Batch Modal */}
      {showSplitModal && (
        <Modal
          isOpen={showSplitModal}
          onClose={() => {
            setShowSplitModal(false)
            setRollsPerBatch('')
          }}
          title="Create Batch from Log"
          size="sm"
        >
          <div className="space-y-6">
            <div className="p-4 bg-brand-50 border border-brand-100 rounded-2xl flex items-center gap-4">
              <div className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center text-brand-600">
                <Scissors size={20} />
              </div>
              <div>
                <p className="text-xs font-bold text-brand-700 uppercase tracking-wider">Cutting Log selected</p>
                <p className="text-sm font-black text-gray-900">
                  {logs.find(l => l.id === selectedIds[0])?.small_rolls_count} Small Rolls Total
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <FormSection label="Batching Preference" description="How would you like to group these rolls?">
                <div className="flex flex-col gap-3">
                  <button 
                    onClick={() => {
                      setRollsPerBatch('')
                      setShowBatchConfirm(true)
                      setShowSplitModal(false)
                    }}
                    className="flex items-center justify-between p-4 bg-gray-50 border border-gray-100 rounded-2xl hover:border-brand-200 hover:bg-white transition-all text-left"
                  >
                    <div>
                      <p className="text-sm font-bold text-gray-800">Single Batch</p>
                      <p className="text-[11px] text-gray-500">Group all rolls into one batch (default)</p>
                    </div>
                    <div className="w-8 h-8 rounded-full bg-white border border-gray-100 flex items-center justify-center text-gray-400">
                      <Check size={16} />
                    </div>
                  </button>

                  <div className="p-4 bg-white border border-brand-100 ring-2 ring-brand-50 rounded-2xl space-y-4">
                    <div>
                      <p className="text-sm font-bold text-gray-800">Split into Multiple Batches</p>
                      <p className="text-[11px] text-gray-500">Automatically create smaller batches</p>
                    </div>
                    
                    <div className="relative">
                      <Input 
                        label="Standard Batch Size (Rolls)"
                        placeholder="e.g. 20"
                        type="number"
                        value={rollsPerBatch}
                        onChange={e => setRollsPerBatch(e.target.value)}
                        autoFocus
                      />
                      {rollsPerBatch && parseInt(rollsPerBatch) > 0 && (
                        <p className="mt-2 text-[10px] font-bold text-brand-600 animate-in fade-in slide-in-from-top-1">
                          System will create {Math.ceil(logs.find(l => l.id === selectedIds[0])?.small_rolls_count / parseInt(rollsPerBatch))} batches.
                        </p>
                      )}
                    </div>

                    <Button 
                      variant="primary" 
                      className="w-full"
                      disabled={!rollsPerBatch || parseInt(rollsPerBatch) <= 0 || parseInt(rollsPerBatch) >= (logs.find(l => l.id === selectedIds[0])?.small_rolls_count)}
                      onClick={handleCreateBatch}
                      isLoading={batching}
                    >
                      Process & Split Batches
                    </Button>
                  </div>
                </div>
              </FormSection>
            </div>

            <Button variant="secondary" onClick={() => setShowSplitModal(false)} className="w-full">
              Nevermind, Cancel
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
